import { useEffect, useState } from "react";
import { Spotip17anFunctions } from "../../../../utils/api";
import SquareImage from "../../../shared/square-image/square-image";
import { AlbumsSuggestion, ArtistDTO, Song } from "../../spotipitan/types";
import ItemsHorizontalScroll from "../../../squadDiskShared/itemsHorizontalScroll/itemsHorizontalScroll";
import ItemDetailsToView from "../../../squadDiskShared/itemDetailsView/itemDetailsToView";
import './spotipitan-home.scss';
import ImageMosaic from "../../../shared/image-mosaic/image-mosaic";
import { useNavigate } from "react-router-dom";
import useUserStore from "../../../../store/UserState";
import { LoginHome } from "../../../users";
import { getPlaylistBdStatus, getPlaylistsFromDB } from "../../../../utils/dbUtils";
import ItemsCloudView from "../../../shared/items-cloud";
import { PaginatedResponse } from "../../../../utils/apiTypes";
import { SongCard } from "../../spotipitan/song-list/song-card";
import { getDateString } from "../../../../utils/utils";
import { SongList } from "../../spotipitan/song-list";
import { isMobileDevice } from "../../../../utils/device-utils";

const SpotipitanHome = () => {

    const [recentPlaylists, setRecentPlaylists] = useState<ArtistDTO[]>([]);
    const [artists, setArtists] = useState<ArtistDTO[]>([]);
    const [playlists, setPlaylists] = useState<ArtistDTO[]>([]);
    const { state: { user, loading: loadingUser, offlineMode } } = useUserStore(state => state);
    const [unreleasedSongs, setUnreleasedSongs] = useState<Song[]>([]);
    const [lastReleasedSongs, setLastReleasedSongs] = useState<Song[]>([]);
    const [suggestions, setSuggestions] = useState<AlbumsSuggestion[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        Spotip17anFunctions.use('getArtists').call().then(playlists => onSetPlaylists(playlists, setArtists)).catch(() => {
        });

        if (offlineMode) {
            return;
        }

        Spotip17anFunctions.use('fetchUnreleasedSongs').call().then((songs: Song[]) => {
            setUnreleasedSongs(songs);
        }).catch(() => {
        });
        Spotip17anFunctions.use('fetchLastReleasedSongs').call().then((songs: Song[]) => {
            setLastReleasedSongs(songs.splice(0, 3));
        }).catch(() => {
        });
        Spotip17anFunctions.use('getYoutubeSuggestions').call().then((suggestions: AlbumsSuggestion[]) => {
            setSuggestions(suggestions);
        }).catch(() => {
        });
    }, [offlineMode]);

    const onSetPlaylists = async (playlists: ArtistDTO[], method: (playlists: ArtistDTO[]) => void) => {
        if (!playlists) return;

        const processedPlaylists: ArtistDTO[] = [];

        for (const playlist of playlists) {
            if (!playlist) continue;
            const processedPlaylist = { ...playlist };
            processedPlaylist.downloaded_status = await getPlaylistBdStatus(playlist.id);
            processedPlaylists.push(processedPlaylist);
        }

        method([...processedPlaylists]);
    }

    useEffect(() => {
        if (!user?.uid || offlineMode) {
            return;
        }

        Spotip17anFunctions.use('getUserPlaylistHistory').call({ max: 6 }).then((playlists: PaginatedResponse<ArtistDTO>) => onSetPlaylists(playlists.data, setRecentPlaylists)).catch(() => {
        });
    }, [user, offlineMode]);

    useEffect(() => {
        if (offlineMode) {
            getPlaylistsFromDB().then(playlists => {
                onSetPlaylists(playlists, setPlaylists);
            });
        }
    }, [offlineMode]);

    const playlistCard = (artist: ArtistDTO, navigateUrl: string, includeDownloadStatus = true, showHover = true, size = 110, hideHoverOnPhone = false) => {
        return (
            <ItemDetailsToView key={artist.id}
                className={"spotipitan-home__horizontal-scroll-item"}
                description={(artist.songs_count ?? artist.songs?.length) + " canciones"}
                title={artist.username}
                onClick={() => navigate(navigateUrl)}
                showHover={showHover} iconClass=""
                disableHover={hideHoverOnPhone && !!isMobileDevice()}
                size={size}
                upperRight={includeDownloadStatus &&
                    <>
                        {artist.downloaded_status === 'downloading' &&
                            <i className="fa-solid fa-spinner fa-spin fa-lg"></i>}
                        {artist.downloaded_status === 'updated' &&
                            <i className="fa-solid fa-circle-down fa-lg"></i>}
                        {artist.downloaded_status === 'downloaded' &&
                            <i className="fa-solid fa-circle-check fa-lg"></i>}
                        {artist.downloaded_status === 'error' &&
                            <i className="fa-solid fa-circle-exclamation fa-lg"></i>}
                    </>}
                image={artist.poster_url || artist.songs?.length === 1 ? (
                    <SquareImage playlist={artist} song={artist.poster_url ? undefined : artist.songs?.[0]} style={{ width: size }} />
                ) : (
                    <ImageMosaic songs={artist.songs?.slice(0, 4) || []} />
                )}
            />
        )
    }

    if (offlineMode) {
        return (
            <div className="spotipitan-home">
                <div className="container">
                    <div className="alert alert-info" role="alert">
                        Estás en modo sin conexión.
                    </div>
                </div>

                <div style={{ height: 'fit-content' }}>
                    <div className="container d-flex flex-row justify-content-between">
                        <div className="d-flex flex-row">
                            <div className="spotipitan-home__items-title" style={{
                                cursor: 'default',
                            }}>
                                Listas offline
                            </div>
                        </div>
                        <div>
                            <button onClick={() => {
                                navigate('/offline-playlists');
                            }} className="spotipitan-home__items-title">
                                <i className="fa-solid fa-list"></i>
                            </button>
                        </div>
                    </div>
                    <ItemsHorizontalScroll className="container spotipitan-home__items-container">
                        {playlists.map((artist) => {
                            return playlistCard(artist, `playlist/${artist.id}`, false);
                        })}
                    </ItemsHorizontalScroll>
                </div>
            </div>


        );
    }

    return (
        <>
            <div className="spotipitan-home">
                {(!user?.uid && !loadingUser) &&
                    <div className="container">
                        <div className="alert alert-light" role="alert">
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}>
                                <div style={{
                                    fontWeight: 500,
                                }}>
                                    Inicia sesión para ver tus <u>listas de reproducción</u>, añadir canciones
                                    a <u>favoritos</u>, ver <u>videos</u>, y más.
                                </div>
                                <div>
                                    <button data-bs-toggle="collapse" type="button" role="button"
                                        data-bs-target="#collapseHomeLogin" id={"btnHomeLogin"}
                                        className="btn btn-primary">Iniciar
                                        sesión
                                    </button>
                                </div>
                            </div>
                            <div className="collapse" id="collapseHomeLogin">
                                <div className="fade custom-modal modal show" style={{
                                    position: 'initial',
                                    opacity: 1,
                                    display: 'block',
                                    height: 'fit-content',
                                }}>
                                    <div className="modal-dialog modal-dialog-centered">
                                        <div className="modal-content">
                                            <LoginHome
                                                onClose={() => document.getElementById('btnHomeLogin')?.click()} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
                <div className="container spotipitan-home__items-container spotipitan-home__playlist-history"
                    style={{
                        height: `calc(${Math.ceil(recentPlaylists.length / 2) * 60}px + ${Math.ceil(recentPlaylists.length / 2) * 0.5}rem)`,
                        opacity: recentPlaylists.length > 0 ? 1 : 0,
                    }}>
                    <ItemsCloudView items={recentPlaylists.map(playlist => {
                        return {
                            id: playlist.id + "",
                            name: playlist.username,
                            imageUrl: playlist.poster_url ? [playlist.poster_url] :
                                (playlist.songs?.slice(0, Math.min(playlist.songs?.length || 0, 4)).map(x => x.poster_url) || []),
                        }
                    })} onItemClick={(itemId) => {
                        const item = recentPlaylists.find(p => String(p.id) === String(itemId));

                        if (itemId === 'all-songs') {
                            navigate(`all-songs`);
                            return;
                        }

                        // Explicit type checks from backend
                        if (item?.type === 'artist') {
                            navigate(`artist/${itemId}`);
                            return;
                        }

                        if (item?.type === 'playlist' || item?.type === 'album') {
                            navigate(`playlist/${itemId}`);
                            return;
                        }

                        // Fallback when item or item.type is not loaded
                        const isArtist = itemId.startsWith('UC') ||
                            itemId.startsWith('dz-artist-') ||
                            itemId.startsWith('sp-artist-') ||
                            itemId.startsWith('spotify:artist:') ||
                            (!isNaN(Number(itemId)) && itemId.length < 5) ||
                            (itemId.length === 22 && /^[0-9A-Za-z]{22}$/.test(itemId));

                        if (isArtist) {
                            navigate(`artist/${itemId}`);
                        } else {
                            navigate(`playlist/${itemId}`);
                        }
                    }} />
                </div>
                {!!unreleasedSongs?.length && <div style={{ height: 'fit-content' }}>
                    <div className="container spotipitan-home__song-list">
                        <div className="spotipitan-home__items-title" style={{
                            cursor: 'default',
                        }}>
                            Próximos estrenos
                        </div>
                        {
                            unreleasedSongs.map((song) => (
                                <SongCard song={song} key={song.id} ableHovering={false} extraDetails={
                                    <div style={{
                                        fontWeight: "bold",
                                        letterSpacing: "0.1px",
                                        fontSize: "0.8rem",
                                        overflow: "hidden",
                                        color: "var(--color-gray-muted)"
                                    }}>
                                        {song.user.username}.
                                        Releasing {getDateString(new Date(song.releaseDate!))}
                                    </div>
                                } />
                            ))
                        }
                    </div>
                </div>}
                {!!lastReleasedSongs?.length && <div style={{ height: 'fit-content' }}>
                    <div className="container spotipitan-home__song-list">
                        <div className="spotipitan-home__items-title" style={{
                            cursor: 'default',
                        }}>
                            Últimos estrenos
                        </div>
                        <SongList
                            style={{
                                paddingBottom: 0,
                            }} songCardHeight={60}
                            canScrollToQueue loadingSongs={false} songs={lastReleasedSongs} showSearch={false} />
                    </div>
                </div>}

                <div style={{ height: 'fit-content' }}>
                    <div className="container">
                        <div className="spotipitan-home__items-title" style={{
                            cursor: 'default',
                        }}>
                            Artistas Spotip17an
                        </div>
                    </div>
                    <div className="container spotipitan-home__items-container">
                        <ItemsCloudView items={artists.map(artist => {
                            return {
                                id: artist.id + "",
                                name: artist.username,
                                imageUrl: artist.poster_url ? [artist.poster_url] :
                                    artist.songs!
                                        .slice(0, (Math.min(artist.songs!.length, 4)))
                                        .map(x => x.poster_url),
                            }
                        })} onItemClick={(itemId) => {
                            if (itemId === 'all-songs') {
                                navigate(`all-songs`);
                            } else if (itemId.length > 5) {
                                navigate(`playlist/${itemId}`);
                            } else {
                                navigate(`artist/${itemId}`);
                            }
                        }} />
                    </div>
                </div>
                {suggestions.length > 0 &&
                    suggestions.map((suggestion, suggestionIndex) => (
                        <div key={suggestion.name} style={{ height: 'fit-content' }}>
                            <div className="container">
                                <div className="spotipitan-home__items-title" style={{
                                    cursor: 'default',
                                }}>
                                    {suggestion.name}
                                </div>
                            </div>
                            <ItemsHorizontalScroll className="container spotipitan-home__items-container">

                                {suggestion.playlists.map((playlist, index) => (
                                    playlistCard(playlist, `playlist/${playlist.id}`, true, suggestionIndex === suggestions.length - 1, 130, suggestionIndex !== suggestions.length - 1))
                                )}
                            </ItemsHorizontalScroll>
                        </div>
                    ))}
            </div>
        </>
    );

}

export default SpotipitanHome;
